import { SettingOutlined } from "@ant-design/icons"
import {
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors
} from "@dnd-kit/core"
import {
  restrictToHorizontalAxis,
  restrictToVerticalAxis
} from "@dnd-kit/modifiers"
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from "@dnd-kit/sortable"
import { App, Button, type TableProps } from "antd"
import type { ColumnsType } from "antd/es/table"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { setError } from "../../containers/User/redux/ui-preferences.slice"
import { useAppDispatch } from "../../hooks/use-app-dispatch"
import { useAppSelector } from "../../hooks/use-app-selector"
import { useDebouncedCallback } from "../../hooks/use-debounced-callback"
import {
  isExpandColumn,
  useTableColumnConfig
} from "../../hooks/use-table-column-config"
import { cn } from "../../utils/cn"
import { DragHandle } from "../DragHandle"
import Table from "../Table"
import { TableColumnConfigModal } from "../TableColumnConfigModal/TableColumnConfigModal"
import {
  EntityTableHeaderCell,
  type EntityTableHeaderCellProps
} from "./EntityTableHeaderCell"
import {
  MIN_COLUMN_WIDTH,
  applyWidthsToConfig,
  reorderConfig
} from "./entity-table.utils"
import "./styles.less"
import { shiftSummaryForSelection } from "./summary-with-selection"
import { selectionResetToken, useRowSelection } from "./use-row-selection"

export interface EntityTableProps<T> extends Omit<TableProps<T>, "columns"> {
  surface: string
  tab?: string
  columns: ColumnsType<T>
  configTitle?: string
  // Bypass column customization (raw default columns, no reorder/resize/config
  // button). Only for tables whose inline-edit row still indexes `children`
  // positionally, which lines up with the default column order alone; rows built
  // with `buildEditRowCells` match on column key and need no bypass.
  disabled?: boolean
  // Columns a user only sees after switching them on in Table settings. A saved
  // layout that names one of them wins either way.
  defaultHiddenColumns?: readonly string[]
  // Keep resize and per-user widths, but pin the column order and visibility.
  // For tables whose summary row or edit row places cells at fixed positions:
  // reordering or hiding a column would move a total under the wrong heading.
  // Unlike `disabled` this keeps the resize handles, so the table still meets
  // EAD-267.
  lockColumnLayout?: boolean
  // Forwarded to the underlying Table wrapper: suppresses its built-in
  // paginator, which is what an infinite-scroll table wants.
  withOutPagination?: boolean
  // Also the wrapper's: it merges this into the paginator's own config.
  showSizeChanger?: boolean
  // Hide the built-in "Customize" button (e.g. when the consumer renders its own
  // trigger elsewhere) and/or control the config modal open state externally.
  hideConfigButton?: boolean
  configControl?: { open: boolean; onOpenChange: (open: boolean) => void }
  selectionMode?: "always" | "on-demand"
  // The table's current filters. Only read when `selectionMode` is "on-demand"
  // and the table has not brought its own `rowSelection`: a change to anything
  // that alters which rows come back drops the selection, since
  // `preserveSelectedRowKeys` would otherwise hold rows that are no longer in
  // the result. Paging and re-ordering are ignored. Pass the filters object
  // itself — the token is derived here so a caller cannot get it wrong.
  selectionFilters?: unknown
  // Opt in to dragging body rows. Rows share this component's DndContext —
  // a nested one would swallow the drag and the rows would never move.
  rowSortable?: {
    items: string[]
    onReorder: (orderedIds: string[]) => void
    /** Accessible name for the grip, e.g. "Reorder milestone". */
    handleLabel: string
  }
}

// A column declared narrower than the shared floor (a row-number or checkbox
// column) would otherwise be stuck far wider than it was ever meant to be.
const ABSOLUTE_MIN_COLUMN_WIDTH = 24

const columnFloor = (declared: unknown): number =>
  typeof declared === "number" && declared < MIN_COLUMN_WIDTH
    ? Math.max(ABSOLUTE_MIN_COLUMN_WIDTH, declared)
    : MIN_COLUMN_WIDTH

const POINTER_SENSOR_OPTIONS = { activationConstraint: { distance: 5 } }

const KEYBOARD_SENSOR_OPTIONS = {
  coordinateGetter: sortableKeyboardCoordinates
}

export function EntityTable<T extends object = Record<string, unknown>>({
  surface,
  tab = "default",
  columns,
  configTitle,
  components,
  className,
  disabled = false,
  defaultHiddenColumns,
  lockColumnLayout = false,
  hideConfigButton = false,
  configControl,
  rowSortable,
  selectionMode = "always",
  selectionFilters,
  ...rest
}: EntityTableProps<T>) {
  const {
    columns: configured,
    fullConfig,
    setColumns,
    resetToDefaults
  } = useTableColumnConfig<T>(surface, tab, columns, defaultHiddenColumns)

  const { t } = useTranslation()
  const title: string = configTitle ?? t("entityTable.configureColumns")

  const dispatch = useAppDispatch()
  const saveError = useAppSelector((state) => state.userUIPreferences.error)
  const { message } = App.useApp()
  useEffect(() => {
    if (!saveError) return
    message.error(t("entityTable.saveError"))
    dispatch(setError(null))
  }, [saveError, message, t, dispatch])

  const [menuOpen, setMenuOpen] = useState(false)
  // A locked table must never reach the config modal: that is where columns
  // get reordered and hidden, which is precisely what the lock prevents.
  // Gating only the button would leave a consumer's own trigger a way in.
  const configModalOpen =
    !lockColumnLayout && (configControl ? configControl.open : menuOpen)
  const setConfigModalOpen = configControl
    ? configControl.onOpenChange
    : setMenuOpen
  const [widths, setWidths] = useState<Record<string, number>>({})
  const widthsRef = useRef(widths)
  useEffect(() => {
    widthsRef.current = widths
  }, [widths])

  const resizedKeysRef = useRef<Set<string>>(new Set())

  // Naming PointerSensor replaces dnd-kit's defaults, which include
  // KeyboardSensor — so it has to be re-added, or the grip is focusable and
  // announced as draggable while ignoring the keyboard. UI_UX_GUIDELINES.md
  // §9 requires full keyboard navigation.
  // The options are module constants because `useSensor` memoises on them, and
  // a fresh literal per render hands every header cell new listener identities.
  const sensors = useSensors(
    useSensor(PointerSensor, POINTER_SENSOR_OPTIONS),
    useSensor(KeyboardSensor, KEYBOARD_SENSOR_OPTIONS)
  )

  const fullConfigRef = useRef(fullConfig)
  useEffect(() => {
    fullConfigRef.current = fullConfig
  }, [fullConfig])

  const setColumnsRef = useRef(setColumns)
  useEffect(() => {
    setColumnsRef.current = setColumns
  }, [setColumns])

  const flushWidths = useCallback(() => {
    if (resizedKeysRef.current.size === 0) return
    const pending: Record<string, number> = {}
    resizedKeysRef.current.forEach((key) => {
      const width = widthsRef.current[key]
      if (width != null) pending[key] = width
    })
    resizedKeysRef.current.clear()
    setColumnsRef.current(applyWidthsToConfig(fullConfigRef.current, pending))
  }, [])

  const persistWidths = useDebouncedCallback(flushWidths, 500)

  useEffect(() => () => flushWidths(), [flushWidths])

  const handleResize = useCallback(
    (key: string, width: number) => {
      resizedKeysRef.current.add(key)
      setWidths((prev) => ({ ...prev, [key]: width }))
      persistWidths()
    },
    [persistWidths]
  )

  // Before a resize begins, pin every column to its currently-rendered width.
  // Otherwise, when columns are narrower than the container, a fixed-layout
  // table stretches them — and committing one width makes the rest
  // redistribute, jumping the dragged column out from under the cursor.
  const tableWrapRef = useRef<HTMLDivElement>(null)
  const pinAllWidths = useCallback(() => {
    const root = tableWrapRef.current
    if (!root) return
    const snapshot: Record<string, number> = {}
    root.querySelectorAll<HTMLElement>("th[data-col-key]").forEach((cell) => {
      const colKey = cell.getAttribute("data-col-key")
      if (colKey) {
        snapshot[colKey] = Math.round(cell.getBoundingClientRect().width)
      }
    })
    setWidths((prev) => ({ ...snapshot, ...prev }))
  }, [])

  const [activeRowId, setActiveRowId] = useState<string | null>(null)

  // Only an on-demand table that did not bring its own `rowSelection` reads
  // this token, and stringifying the filters is not free — so the ~26 tables
  // that never consume it skip the work entirely rather than paying it on
  // every render.
  const ownSelectionToken = useMemo(
    () =>
      selectionMode === "on-demand" && !rest.rowSelection
        ? selectionResetToken(selectionFilters)
        : null,
    [selectionMode, rest.rowSelection, selectionFilters]
  )

  // A table that needs the keys — the leads bulk bar — passes its own
  // `rowSelection` and keeps owning them. Everywhere else the selection is
  // only ever shown, never acted on, so holding it here saves every table
  // repeating the same three lines of state.
  const ownRowSelection = useRowSelection(ownSelectionToken)
  const effectiveRowSelection: TableProps<T>["rowSelection"] =
    rest.rowSelection ??
    (selectionMode === "on-demand" ? ownRowSelection : undefined)

  const selectedRowKeys = effectiveRowSelection?.selectedRowKeys
  const hasSelection =
    Array.isArray(selectedRowKeys) && selectedRowKeys.length > 0

  // Revealing the checkbox by unmounting it would put the only way into a
  // selection behind a mouse. It stays in the tree and CSS hides it, so it is
  // still tabbable — and row hover cannot go stale the way tracked state does.
  const onDemandRowSelection = useMemo<TableProps<T>["rowSelection"]>(() => {
    if (selectionMode !== "on-demand" || !effectiveRowSelection) {
      return effectiveRowSelection
    }

    const rowSelection = effectiveRowSelection
    const originalRenderCell = rowSelection.renderCell
    const originalColumnTitle = rowSelection.columnTitle

    return {
      ...rowSelection,
      columnTitle: (checkboxNode) => {
        if (!hasSelection) return null
        if (typeof originalColumnTitle === "function") {
          return originalColumnTitle(checkboxNode)
        }
        return originalColumnTitle ?? checkboxNode
      },
      renderCell: originalRenderCell
    }
  }, [selectionMode, effectiveRowSelection, hasSelection])

  // A totals row is written against the data columns; the checkbox column
  // arrives in front of them.
  //
  // Keyed on `effectiveRowSelection` rather than the mode, so it also covers a
  // `selectionMode="always"` table that brought its own `rowSelection` — that
  // table has a checkbox column too, and its totals row needs the same shift.
  const mergedSummary = useMemo<TableProps<T>["summary"]>(() => {
    const summary = rest.summary
    if (!summary || !effectiveRowSelection) return summary
    return (data: readonly T[]) =>
      shiftSummaryForSelection(summary(data)) as React.ReactNode
  }, [rest.summary, effectiveRowSelection])

  const rowItems = rowSortable?.items
  const onReorderRows = rowSortable?.onReorder
  const handleLabel = rowSortable?.handleLabel
  const rowSortEnabled = !!rowSortable
  const isRowDrag = activeRowId != null

  const activeRecord = useMemo(() => {
    if (!activeRowId) return null

    const key = typeof rest.rowKey === "string" ? rest.rowKey : "id"
    return (
      (rest.dataSource as readonly T[] | undefined)?.find(
        (record) =>
          String((record as Record<string, unknown>)[key]) === activeRowId
      ) ?? null
    )
  }, [activeRowId, rest.dataSource, rest.rowKey])

  // Width of the visible table area, captured when a row is picked up. The
  // overlay copy is clipped to it, otherwise a `scroll: max-content` table
  // renders the floating row at its full, far wider, natural width.
  const [overlayWidth, setOverlayWidth] = useState<number>()

  // Keep the expand-icon column so the overlay's cells line up with the real
  // row, but pin it closed — the expanded content is what previously trailed
  // the dragged row as a stray block.
  const overlayExpandable = useMemo<TableProps<T>["expandable"]>(() => {
    if (!rest.expandable) return undefined

    return {
      ...rest.expandable,
      expandedRowKeys: [],
      onExpand: undefined,
      onExpandedRowsChange: undefined
    }
  }, [rest.expandable])

  const onDragStart = useCallback(
    ({ active }: DragStartEvent) => {
      const id = String(active.id)
      const isRow = !!rowItems?.includes(id)

      if (isRow) {
        setOverlayWidth(tableWrapRef.current?.clientWidth)
      }
      setActiveRowId(isRow ? id : null)
    },
    [rowItems]
  )

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      const draggedRow = activeRowId
      setActiveRowId(null)

      if (!over || active.id === over.id) return

      // Columns and rows share one context, so route on what was picked up.
      if (draggedRow && rowItems && onReorderRows) {
        const from = rowItems.indexOf(String(active.id))
        const to = rowItems.indexOf(String(over.id))
        if (from === -1 || to === -1) return
        return onReorderRows(arrayMove(rowItems, from, to))
      }

      setColumns(reorderConfig(fullConfig, String(active.id), String(over.id)))
    },
    [fullConfig, setColumns, activeRowId, rowItems, onReorderRows]
  )

  const decorated = useMemo<ColumnsType<T>>(
    () =>
      configured.map((col) => {
        // AntD matches its expand marker by identity, so it has to pass through
        // as the very same object — spreading it into a copy leaves an unnamed
        // empty column and makes AntD add its own expand control elsewhere.
        if (isExpandColumn(col)) return col

        const key = col.key == null ? undefined : String(col.key)
        const liveWidth = key ? widths[key] : undefined
        const cellProps: Partial<EntityTableHeaderCellProps> = {
          columnKey: key,
          reorderable: key != null && !lockColumnLayout,
          minWidth: columnFloor(col.width),
          onColumnResize: key != null ? handleResize : undefined,
          onResizeStart: key != null ? pinAllWidths : undefined
        }
        return {
          ...col,
          width: liveWidth ?? col.width,
          // A copy per call: AntD mutates what this returns, so one shared
          // identity accumulated its handler wrappers and a single Enter
          // sorted twice — and left `aria-sort` behind on a cleared sort.
          onHeaderCell: () =>
            ({
              ...cellProps
            }) as unknown as React.HTMLAttributes<HTMLTableCellElement>
        }
      }),
    [configured, widths, handleResize, pinAllWidths, lockColumnLayout]
  )

  // Prepended after the column config is applied, so the grip is invisible to
  // "Customize columns": it cannot be hidden (which would silently kill row
  // reordering) and cannot be dragged out of its pinned leading position.
  const withHandle = useMemo<ColumnsType<T>>(
    () =>
      rowSortEnabled
        ? [
            {
              key: "__row-drag-handle",
              align: "center" as const,
              width: 44,
              fixed: "left" as const,
              render: () => <DragHandle label={handleLabel as string} />
            },
            ...decorated
          ]
        : decorated,
    [rowSortEnabled, decorated, handleLabel]
  )

  const sortableKeys = useMemo(
    () =>
      lockColumnLayout
        ? []
        : configured
            .map((col) => (col.key == null ? null : String(col.key)))
            .filter((colKey): colKey is string => colKey != null),
    [configured, lockColumnLayout]
  )

  // Rows get their own SortableContext around <tbody> rather than one wrapping
  // the whole table: useSortable binds to the nearest context, so nesting one
  // around the table would capture the header cells too and break column drag.
  const SortableBody = useCallback(
    (bodyProps: React.HTMLAttributes<HTMLTableSectionElement>) => (
      <SortableContext
        items={rowItems ?? []}
        strategy={verticalListSortingStrategy}
      >
        <tbody {...bodyProps} />
      </SortableContext>
    ),
    [rowItems]
  )

  const mergedComponents = useMemo<TableProps<T>["components"]>(
    () => ({
      ...components,
      header: { ...(components?.header ?? {}), cell: EntityTableHeaderCell },
      ...(rowSortEnabled
        ? { body: { ...(components?.body ?? {}), wrapper: SortableBody } }
        : {})
    }),
    [components, rowSortEnabled, SortableBody]
  )

  // Render the raw default columns with the consumer's own components — no
  // reorder/resize wrapping and no header-cell override — so positional edit
  // rows stay aligned.
  if (disabled) {
    return (
      <Table<T>
        {...rest}
        className={cn("entity-table", className)}
        columns={columns}
        components={components}
      />
    )
  }

  return (
    <>
      {/* The config modal is how a column gets hidden or reordered, so a
          locked table must not offer it. */}
      {!hideConfigButton && !lockColumnLayout && (
        <div className="flex justify-end px-2 py-1">
          <Button
            size="small"
            icon={<SettingOutlined />}
            onClick={() => setConfigModalOpen(true)}
          >
            {t("entityTable.customize")}
          </Button>
        </div>
      )}
      <div ref={tableWrapRef}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[
            isRowDrag ? restrictToVerticalAxis : restrictToHorizontalAxis
          ]}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragCancel={() => setActiveRowId(null)}
        >
          <SortableContext
            items={sortableKeys}
            strategy={horizontalListSortingStrategy}
          >
            <Table<T>
              {...rest}
              className={cn(
                "entity-table",
                selectionMode === "on-demand" &&
                  "entity-table--selection-on-demand",
                selectionMode === "on-demand" &&
                  hasSelection &&
                  "entity-table--has-selection",
                className
              )}
              columns={withHandle}
              components={mergedComponents}
              rowSelection={onDemandRowSelection}
              summary={mergedSummary}
            />
          </SortableContext>
          <DragOverlay>
            {activeRowId && activeRecord ? (
              // Rendered through `decorated`, not the raw columns, so the copy
              // under the cursor keeps the configured order, visibility and
              // widths of the row it was lifted from.
              // A deliberately bare table: spreading the real table's props
              // pulls in expandable rows, scroll measure rows and selection,
              // which render as stray artifacts under the dragged row.
              <div
                style={{ width: overlayWidth, overflow: "hidden" }}
                className={"shadow-lg"}
              >
                <Table<T>
                  showHeader={false}
                  // The wrapper computes its own pagination and ignores
                  // `pagination={false}`; this is what suppresses the
                  // "showing 1 of 1 results" footer.
                  withOutPagination
                  pagination={false}
                  dataSource={[activeRecord]}
                  columns={withHandle}
                  rowKey={rest.rowKey}
                  size={rest.size}
                  bordered={rest.bordered}
                  expandable={overlayExpandable}
                  className={cn("entity-table", className)}
                />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
      <TableColumnConfigModal
        open={configModalOpen}
        title={title}
        availableColumns={columns}
        currentConfig={fullConfig}
        onSave={setColumns}
        onReset={() => {
          resizedKeysRef.current.clear()
          setWidths({})
          resetToDefaults()
        }}
        onClose={() => setConfigModalOpen(false)}
      />
    </>
  )
}

export default EntityTable
